import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import { FormattedMessage, useIntl } from 'react-intl';
import { GenericMessages } from '../../../intl/GenericMessages';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { IonButton, IonIcon, IonLabel } from '@ionic/react';
import { debounce } from 'lodash';
import { Bundle, BundleSearchResult } from '../../../models/bundle';
import { BundleMessages } from '../../../intl/BundleMessages';
import { BundleSearchRequest, CreateBundleRequest, downloadBundleTemplate } from '../../../redux/api/bundles';
import { Organization } from '../../../models/organization';
import { getOrgName } from '../../../utils/helpers/referenceHelpers';
import isValidTimeZone from '../../../utils/helpers/timeHelpers';
import { useHistory } from 'react-router';
import { BundleUploadModal } from '../bundle-upload-modal/BundleUploadModal';
import './BundlesTable.scss';
import { DqText } from '../../dq-text';
import { arrowDownCircle } from 'ionicons/icons';
import WhiteBundleIcon from '../../../assets/icons/bundle-outline-white.svg';
import { BundleTemplateDownloadButton } from '../BundleTemplateDownloadButton';
import { AppRouteConstants } from '../../../pages/app-layout/AppRouteConstants';

export interface BundleTableProps {
  data: BundleSearchResult;
  organizations: Organization[];
  request: BundleSearchRequest;
  onTableChange: (request: Partial<BundleSearchRequest>) => void;
  onCreateBundle: (bundleInfo: CreateBundleRequest) => void;
}

function buildFilters(data: Bundle[] | undefined, tableState: MUIDataTableState, organizations: Organization[]) {
  const filters: Record<string, string[]> = {};
  if (!tableState?.filterList?.length) {
    return filters;
  }

  for (let i = 0; i < tableState.filterList.length; i++) {
    if (tableState.filterList[i]?.length > 0) {
      const columnName = tableState.columns[i].name;
      const values = tableState.filterList[i];
      if (columnName === 'organizationId') {
        filters.organizationId = values
          .map(name => organizations.find(org => org.orgName === name)?._id)
          .filter(i => !!i) as string[];
      } else if (columnName === 'siteName') {
        filters.siteName = values;
      }
    }
  }
  return filters;
}

export const BundlesTable: React.FC<BundleTableProps> = ({
  data,
  organizations,
  request,
  onTableChange,
  onCreateBundle,
}) => {
  // State/hook setup
  const { formatMessage } = useIntl();
  const history = useHistory();
  const userData = useSelector(selectAuthUserInfo);
  const [columns, setColumns] = useState<MUIDataTableColumn[]>([]);
  const [isBundleUploadModalOpen, setIsBundleUploadModalOpen] = useState<boolean>(false);

  const handleDownload = useCallback(async () => {
    await downloadBundleTemplate();
  }, []);

  // Table setup
  useEffect(() => {
    const organizationFilterName = !!request?.organizationId
      ? organizations?.find(org => org._id === request.organizationId)?.orgName
      : undefined;
    // CAUTION: We need to regenerate a lot of the options whenever data changes, because it's captured
    // in the closure and we need to make sure we use the latest. But we need to consider the previous
    // columns state, specifically which columns have been hidden. So we reinitialize most properties of
    // the columns but lookup the display property from the previous state and respect that.
    //
    // This prevents added columns from being removed when a new page of data loads in.
    setColumns(prev => [
      {
        label: formatMessage(BundleMessages.bundlesTableIdColumn),
        name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
        options: {
          display: false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableNameColumn),
        name: 'name',
        options: {
          filter: false,
          display: prev.find(col => col.name === 'name')?.options?.display ?? true,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableSiteNameColumn),
        name: 'siteName',
        options: {
          filter: true,
          filterOptions: {
            names: data?.relations?.siteNames ?? [],
          },
          display: prev.find(col => col.name === 'siteName')?.options?.display ?? true,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableTubeCountColumn),
        name: 'tubeCount',
        options: {
          display: prev.find(col => col.name === 'tubeCount')?.options?.display ?? true,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableOrganizationColumn),
        name: 'organizationId',
        options: {
          customBodyRender: (value: string) => getOrgName(value, organizations),
          filterOptions: {
            names: organizations?.map(org => org.orgName) ?? [],
          },
          filterList: organizationFilterName ? [organizationFilterName] : [],
          sort: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableUploadDateColumn),
        name: 'createdAt',
        options: {
          customBodyRender: (value: string | number | Date, tableMeta: { rowData: (string | undefined)[] }) => {
            if (value && tableMeta?.rowData[5] && isValidTimeZone(tableMeta?.rowData[5])) {
              return new Date(value).toLocaleString('en-US', { timeZone: tableMeta.rowData[5], timeZoneName: 'short' });
            } else if (value) {
              return new Date(value).toLocaleString();
            } else {
              return formatMessage(GenericMessages.unknownPlaceholder);
            }
          },
          display: prev.find(col => col.name === 'createdAt')?.options?.display ?? true,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableTubeDiameterColumn),
        name: 'tubeDiameter',
        options: {
          display: prev.find(col => col.name === 'tubeDiameter')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableHeatExchangeModelNumberColumn),
        name: 'heatExchangerModelNumber',
        options: {
          display: prev.find(col => col.name === 'heatExchangerModelNumber')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableAssetNumberColumn),
        name: 'assetNumber',
        options: {
          display: prev.find(col => col.name === 'assetNumber')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTablePatternColumn),
        name: 'pattern',
        options: {
          display: prev.find(col => col.name === 'pattern')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTablePitchColumn),
        name: 'pitch',
        options: {
          display: prev.find(col => col.name === 'pitch')?.options?.display ?? false,
          filter: false,
        },
      },
      {
        label: formatMessage(BundleMessages.bundlesTableSharedColumn),
        name: 'shared',
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const rowData = data?.results[dataIndex];
            if (!rowData) return;
            const shared = rowData?.organizationId !== userData?.org || rowData.permissions?.length;
            return formatMessage(shared ? GenericMessages.yesButtonLabel : GenericMessages.noButtonLabel);
          },
          display: prev.find(col => col.name === 'shared')?.options?.display ?? false,
          filter: false,
        },
      },
    ]);
  }, [formatMessage, data, userData?.org, organizations, request.organizationId, request.siteName]);

  /**
   * Debounce a function for updating the search term query, we don't want
   * to fire off a request every key-press
   */
  const setSearchTerm = useMemo(
    () =>
      debounce(
        (searchTerm: string | undefined) => {
          onTableChange({
            searchTerm: searchTerm,
          });
        },
        250,
        { maxWait: 1000 },
      ),
    [onTableChange],
  );

  const handleTableChange = useCallback(
    (eventType: string, tableState: MUIDataTableState) => {
      switch (eventType) {
        case 'viewColumnsChange':
          // It will only accept data like this, but it sends it back flattened
          const tempColumns = tableState.columns.map(column => ({ ...column, options: { ...column } }));
          setColumns(tempColumns);
          break;
        case 'sort':
          onTableChange({
            sort: { property: tableState.sortOrder.name as keyof Bundle, order: tableState.sortOrder.direction },
            pageNumber: 0,
            pageSize: tableState.rowsPerPage,
          });
          break;
        case 'changePage':
          onTableChange({
            pageNumber: tableState.page,
            pageSize: tableState.rowsPerPage,
          });
          break;
        case 'changeRowsPerPage':
          onTableChange({
            pageNumber: tableState.page,
            pageSize: tableState.rowsPerPage,
          });
          break;
        case 'filterChange':
        case 'resetFilters':
          const filters = buildFilters(data?.results, tableState, organizations ?? []);
          onTableChange({
            pageNumber: tableState.page,
            pageSize: tableState.rowsPerPage,
            organizationId: filters?.organizationId?.length > 0 ? filters?.organizationId[0] : undefined,
            siteName: filters?.siteName?.length > 0 ? filters?.siteName[0] : undefined,
          });
          break;
        case 'search':
          setSearchTerm(tableState.searchText ?? undefined);
          break;
      }
    },
    [data?.results, onTableChange, organizations, setSearchTerm],
  );

  const handleView = (data: any) => {
    // data[0] holds the bundle id used to fetch bundle from API
    history.push(AppRouteConstants.BundleDetails.replace(':id', data[0]));
  };

  const onSave = async (bundleInfo?: CreateBundleRequest) => {
    setIsBundleUploadModalOpen(false);
    if (!bundleInfo) {
      return;
    }

    onCreateBundle(bundleInfo);
  };

  const options: {} = {
    selectableRows: 'none',
    responsive: 'standard',
    download: false,
    print: false,
    serverSide: true,
    onRowClick: handleView,
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    rowsPerPageOptions: [10, 25, 50],
    count: data?.totalCount,
    pagination: !!data?.totalCount,
    customToolbar: () => (
      <>
        <BundleTemplateDownloadButton />
        <IonButton onClick={async () => setIsBundleUploadModalOpen(true)} color="primary">
          {formatMessage(BundleMessages.uploadBundleButtonLabel)}
        </IonButton>
      </>
    ),
    textLabels: {
      body: {
        noMatch: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {!!request?.searchTerm || request?.siteName ? (
              <div className="search-message">
                <DqText variant="h3">{formatMessage(BundleMessages.noBundlesFoundMessage)}</DqText>
              </div>
            ) : (
              <div className="empty-or-premium-bundle-card-content">
                <div className="empty-or-premium-content-body">
                  <IonIcon className="empty-or-premium-bundle-icon" md={WhiteBundleIcon} color="dark" />
                  <div className="search-message">
                    <DqText variant="h3">{formatMessage(BundleMessages.bundleProfilesTableEmptyResults)}</DqText>
                  </div>
                </div>
                <div className="download-bundle-template-button">
                  <IonButton onClick={handleDownload} color="primary" fill="clear">
                    <IonIcon slot="start" icon={arrowDownCircle} />
                    <IonLabel>
                      <FormattedMessage {...BundleMessages.downloadTemplateButtonLabel} />
                    </IonLabel>
                  </IonButton>
                </div>
              </div>
            )}
          </div>
        ),
        toolTip: formatMessage(GenericMessages.dataTablesSort),
      },
      pagination: {
        next: formatMessage(GenericMessages.dataTablesNextPage),
        previous: formatMessage(GenericMessages.dataTablesPreviousPage),
        rowsPerPage: formatMessage(GenericMessages.dataTablesRowsPerPage),
        displayRows: formatMessage(GenericMessages.of),
      },
      toolbar: {
        search: formatMessage(GenericMessages.searchPlaceholder),
        viewColumns: formatMessage(GenericMessages.dataTablesViewColumns),
        filterTable: formatMessage(GenericMessages.dataTablesFilterTable),
      },
      filter: {
        all: formatMessage(GenericMessages.dataTablesAll),
        title: formatMessage(GenericMessages.dataTablesFilters),
        reset: formatMessage(GenericMessages.dataTablesReset),
      },
      viewColumns: {
        title: formatMessage(GenericMessages.dataTablesShowColumns),
      },
    },
  } as MUIDataTableOptions;
  return (
    <>
      <MUIDataTable columns={columns} data={data.results} options={options} title={undefined} />

      <BundleUploadModal isOpen={isBundleUploadModalOpen} handleSave={onSave} />
    </>
  );
};
